// Azure AD (Entra ID) MSAL config for ValueQuest.
// Ported from vq-portal/src/auth/msalConfig.js. Same client ID + tenant
// (`vq-portal` app registration on the VQ Entra tenant). New redirect URIs
// for the nexus-webui SWA origins must be added to the app registration in
// Azure portal before login will succeed in stage/prod.

export const msalConfig = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_AD_CLIENT_ID || "eec54015-7f0c-4f55-9311-7e8ec0bcf4ec",
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_AD_TENANT_ID || "9bde290f-1915-4626-a072-d8ddb3149aaf"}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false,
  },
};

export const loginRequest = {
  scopes: ["User.Read"],
};

// Unused: groups now come only from core.portal_groups (GET /api/groups/,
// managed in Portal Admin). This hard-coded fallback went stale (missing
// groups, no is_active), so it is switched off. Kept commented for reference.
/*
// Azure AD Security Group GUID → friendly name.
const GROUP_MAP = {
  "72a122bc-d3df-49c6-aa76-c280e573f062": "portal-management",
  "72c122bb-4f6c-451b-8789-248c8415960d": "portal-admin",
  "16f99298-7f8d-424a-bd80-301017301b3a": "portal-it",
  "d1421af1-3ec6-4c52-be57-d724381367e3": "portal-it-support",
  "ec8b751b-92c1-4254-9052-fb6ef7bee18b": "portal-it-support-admin",
  "6983300d-d487-4889-808d-5053d2425795": "portal-research",
  "7e3e7d8d-dd32-4d4d-b201-c977411b9a86": "portal-operations",
  "2af21569-d39f-4222-8bbb-23869242ebda": "portal-operations-admin",
  "959d6664-1e3e-40dc-bc68-96ff84dfd4e1": "portal-sales",
  "8f016b71-df19-41a7-9411-9a89daea09de": "portal-finance",
  "ec7d1deb-715e-4e90-b464-67e7c5d9c0cd": "portal-legal",
  "567b0d28-0b6b-4377-bd2d-3c500e92aa0d": "portal-hr",
  "0c4d43d8-e4bd-4865-bda7-927ae537acd0": "portal-marketing",
  "2739cf64-cbb2-486c-a569-79c52bf879a1": "portal-pe-partners",
  "09dcfeb4-540e-4454-a7c3-c91a8dc9ab0e": "portal-pe-investment-admin",
  "5307bdf8-a041-470d-b3d4-2396972c23e3": "portal-pe-deal-admin",
  "7cfeb55d-27ce-4101-9c1c-a1a0da2f0f66": "portal-pe-investment-team",
  "b3df53bb-7782-4b87-950b-9260e8b94273": "portal-pe-deal-team",
  "2edb9f03-0b05-4437-b5cf-4ea6680422a1": "portal-fm-admin",
  "0ebc45b7-2799-490e-8652-de079b5a5027": "portal-srm"
};

// Display config for groups (used in header profile dropdown).
export const GROUP_LABELS = {
  "portal-management":          { label: "Management",           icon: "👑" },
  "portal-admin":               { label: "Admin",                icon: "🔑" },
  "portal-fm-admin":            { label: "FM Admin",             icon: "📊" },
  "portal-it":                  { label: "IT",                   icon: "🖥️" },
  "portal-it-support":          { label: "IT Support",           icon: "🛡" },
  "portal-it-support-admin":    { label: "IT Support Admin",     icon: "🛡" },
  "portal-research":            { label: "Research",             icon: "🔬" },
  "portal-operations":          { label: "Operations",           icon: "⚙️" },
  "portal-operations-admin":    { label: "Ops Admin",            icon: "🛠️" },
  "portal-sales":               { label: "Sales",                icon: "📈" },
  "portal-srm":                 { label: "SRM",                  icon: "📨" },
  "portal-finance":             { label: "Finance",              icon: "💰" },
  "portal-legal":               { label: "Legal",                icon: "⚖️" },
  "portal-hr":                  { label: "HR",                   icon: "👥" },
  "portal-pe-partners":         { label: "PE Partners",          icon: "👔" },
  "portal-pe-investment-admin": { label: "PE Investment Admin",  icon: "📊" },
  "portal-pe-deal-admin":       { label: "PE Deal Admin",        icon: "🗃️" },
  "portal-pe-investment-team":  { label: "PE Investment Team",   icon: "💰" },
  "portal-pe-deal-team":        { label: "PE Deal Team",         icon: "🤝" },
};
*/

/**
 * The user's groups from the token, as [{name, label, icon}].
 *
 * registryGroups = GET /api/groups/ rows (core.portal_groups, managed in
 * Portal Admin): active groups only, in their sort_order. Empty until that
 * list has loaded (or if it fails) — the profile then shows no groups.
 */
export function getUserGroups(account, registryGroups) {
  if (!registryGroups?.length) return [];

  const ids = (account?.idTokenClaims?.groups || []).map((id) => id.toLowerCase());
  const byGuid = new Map(
    registryGroups
      .filter((g) => g.is_active)
      .map((g) => [g.group_guid.toLowerCase(), g])
  );
  return ids
    .map((id) => byGuid.get(id))
    .filter(Boolean)
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((g) => ({ name: g.group_name, label: g.display_label, icon: g.icon || "•" }));

  // Former fallback (switched off with GROUP_MAP / GROUP_LABELS above):
  // return ids
  //   .map((id) => GROUP_MAP[id])
  //   .filter(Boolean)
  //   .map((name) => ({ name, ...(GROUP_LABELS[name] || { label: name, icon: "•" }) }));
}

// Unused: nothing in the shell calls getUserRoleInfo (GROUP_PRIORITY only
// served it). Group order now comes from core.portal_groups.sort_order.
// Kept commented for reference.
/*
const GROUP_PRIORITY = [
  "portal-management", "portal-admin", "portal-fm-admin", "portal-it",
  "portal-it-support-admin", "portal-it-support", "portal-research",
  "portal-operations-admin", "portal-operations", "portal-sales",
  "portal-srm", "portal-finance", "portal-pe-partners", "portal-pe-investment-admin",
  "portal-pe-deal-admin", "portal-pe-investment-team","portal-pe-deal-team",
  "portal-legal", "portal-hr", "portal-marketing",
];

// Highest-ranked group: registry sort_order when loaded, else GROUP_PRIORITY.
export function getUserRoleInfo(account, registryGroups) {
  const groups = getUserGroups(account, registryGroups);
  if (registryGroups?.length) return groups[0] || { label: "Member", icon: "👤" };
  for (const g of GROUP_PRIORITY) {
    const hit = groups.find((x) => x.name === g);
    if (hit) return hit;
  }
  return { label: "Member", icon: "👤" };
}
*/

export function getUserName(account) {
  return account?.name || account?.username?.split("@")[0] || "User";
}
